import React, { useState, useCallback, useRef } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import { Sparkles, Pencil, Check, X, MessageSquare, Variable, Brain, Plus, Trash2 } from "lucide-react";
import VariableDropdown from "../FlowBuilder/VariableDropdown";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";

// ─── Collapsed View ─────────────────────────────────────
const CollapsedView = ({ data, onEdit }) => {
  const entities = data.entities || [];
  const hasEntities = entities.length > 0;

  return (
    <div
      className="relative rounded-xl overflow-hidden"
      style={{ backgroundColor: "#F3E8FF", padding: "10px", minWidth: "260px", maxWidth: "300px" }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:shadow-lg"
        title="Edit node"
      >
        <Pencil size={12} className="text-purple-600" />
      </button>

      <div className="bg-white rounded-lg shadow-sm p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 bg-purple-100 rounded-lg">
            <Sparkles size={14} className="text-purple-600" />
          </div>
          <span className="text-[12px] font-bold text-purple-900">AI Ask Question</span>
        </div>

        {data.question ? (
          <div className="flex items-start gap-1.5 mb-2">
            <MessageSquare size={11} className="text-purple-400 mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-gray-700 leading-tight">
              {data.question.length > 80 ? data.question.substring(0, 80) + "..." : data.question}
            </p>
          </div>
        ) : (
          <p className="text-[11px] text-gray-400 italic mb-2">No question set</p>
        )}

        {hasEntities ? (
          <div className="space-y-1">
            {entities.map((ent) => (
              <div key={ent.id} className="flex items-center gap-1.5">
                <Variable size={9} className="text-[#008069] flex-shrink-0" />
                <span className="text-[10px] font-mono font-semibold text-[#008069]">{`{{${ent.variableName || "?"}}}`}</span>
                {ent.required && <span className="text-[8px] text-purple-500">required</span>}
              </div>
            ))}
            <div className="mt-1.5 text-[9px] text-purple-600 bg-purple-50 px-2 py-0.5 rounded inline-block">
              {entities.length} entit{entities.length === 1 ? "y" : "ies"}
            </div>
          </div>
        ) : (
          <>
            {data.variableName && (
              <div className="flex items-center gap-1.5 mb-1.5">
                <Variable size={10} className="text-[#008069]" />
                <span className="text-[10px] font-mono font-semibold text-[#008069]">{`{{${data.variableName}}}`}</span>
              </div>
            )}
            {data.aiPrompt && (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-purple-50 rounded-lg">
                <Brain size={10} className="text-purple-500" />
                <span className="text-[9px] text-purple-600 font-medium truncate">
                  {data.aiPrompt.length > 50 ? data.aiPrompt.substring(0, 50) + "..." : data.aiPrompt}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── Expanded Editor ────────────────────────────────────
const ExpandedEditor = ({ data, onSave, onCancel, flowVariables, onCreateVariable }) => {
  const [form, setForm] = useState({
    question: data.question || "",
    variableName: data.variableName || "",
    aiPrompt: data.aiPrompt || "",
    answerType: data.answerType || "text",
    entities: (data.entities || []).map((e) => ({
      ...e,
      id: e.id || `ent_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
      fallbackMessage: e.fallbackMessage ?? "",
      required: e.required !== false,
    })),
  });

  const questionRef = useRef(null);
  const promptRefs = useRef([]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const insertVariableIntoQuestion = (varName) => {
    if (!varName) return;
    const current = form.question || "";
    const placeholder = `{{${varName}}}`;
    const textarea = questionRef.current;
    if (textarea) {
      const pos = textarea.selectionStart ?? current.length;
      const before = current.slice(0, pos);
      const after = current.slice(pos);
      updateField("question", before + placeholder + after);
    } else {
      updateField("question", current + placeholder);
    }
  };

  const addEntity = () => {
    setForm((prev) => ({
      ...prev,
      entities: [
        ...prev.entities,
        {
          id: `ent_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
          variableName: "",
          prompt: "",
          fallbackMessage: "",
          required: true,
        },
      ],
    }));
  };

  const updateEntity = (idx, field, value) => {
    setForm((prev) => {
      const updated = [...prev.entities];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, entities: updated };
    });
  };

  const removeEntity = (idx) => {
    setForm((prev) => ({ ...prev, entities: prev.entities.filter((_, i) => i !== idx) }));
  };

  const insertVariableForEntity = (idx, varName) => {
    if (!varName) return;
    const current = form.entities[idx]?.prompt || "";
    const placeholder = `{{${varName}}}`;
    const textarea = promptRefs.current[idx];
    if (textarea) {
      const pos = textarea.selectionStart ?? current.length;
      const before = current.slice(0, pos);
      const after = current.slice(pos);
      updateEntity(idx, "prompt", before + placeholder + after);
    } else {
      updateEntity(idx, "prompt", current + placeholder);
    }
  };

  const handleSave = () => {
    const payload = {
      question: form.question,
      variableName: form.variableName,
      aiPrompt: form.aiPrompt,
      answerType: form.answerType,
      entities: form.entities,
    };
    if (form.entities.length > 0) {
      payload.variableName = payload.variableName || form.entities[0].variableName;
      payload.aiPrompt = payload.aiPrompt || form.entities[0].prompt;
    }
    onSave(payload);
  };

  const hasEntities = form.entities.length > 0;

  return (
    <div
      className="nowheel"
      style={{ backgroundColor: "#F3E8FF", borderRadius: "12px", padding: "14px", minWidth: "340px", maxWidth: "420px" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-purple-200 rounded-lg">
            <Sparkles size={14} className="text-purple-700" />
          </div>
          <span className="text-[13px] font-bold text-purple-900">AI Ask Question</span>
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={handleSave}
            className="px-3 py-1.5 text-[11px] font-bold bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1 shadow-sm"
          >
            <Check size={12} />
            Save
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-[11px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>

      <NodeEditorTabs nodeType="aiAskQuestion">
      <div className="bg-white rounded-lg shadow-sm p-3 space-y-3 max-h-[480px] overflow-y-auto">
        {/* Question + variable dropdown */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider">
              <MessageSquare size={10} />
              Question to ask
            </label>
            <VariableDropdown
              value=""
              onChange={insertVariableIntoQuestion}
              flowVariables={flowVariables}
              placeholder="Insert variable"
              className="w-32"
            />
          </div>
          <textarea
            ref={questionRef}
            value={form.question}
            onChange={(e) => updateField("question", e.target.value)}
            placeholder="What is your name and email?"
            rows={3}
            className="w-full px-3 py-2 text-[12px] text-gray-800 bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-purple-400 resize-none placeholder:text-gray-400"
          />
        </div>

        {/* Entities to extract */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">Entities to extract</label>
            <button
              onClick={addEntity}
              className="text-[9px] font-bold text-purple-600 hover:text-purple-700 flex items-center gap-0.5"
            >
              <Plus size={9} /> Add Entity
            </button>
          </div>

          {form.entities.length === 0 && (
            <p className="text-[9px] text-gray-400 italic">No entities. Add entities to extract multiple fields (e.g. name, email).</p>
          )}

          {form.entities.map((ent, idx) => (
            <div key={ent.id} className="bg-gray-50 rounded-lg p-2.5 mb-2">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[9px] font-bold text-gray-500 uppercase">Entity #{idx + 1}</span>
                <button type="button" onClick={() => removeEntity(idx)} className="p-0.5 text-red-400 hover:text-red-600">
                  <Trash2 size={10} />
                </button>
              </div>

              <div className="mb-2">
                <label className="flex items-center gap-1 text-[9px] font-bold text-gray-500 mb-0.5">
                  <Variable size={8} /> Save to variable
                </label>
                <VariableDropdown
                  value={ent.variableName}
                  onChange={(name) => updateEntity(idx, "variableName", name)}
                  flowVariables={flowVariables}
                  allowCreate
                  onCreateVariable={onCreateVariable}
                  placeholder="Select variable..."
                />
              </div>

              <div className="mb-2">
                <div className="flex items-center justify-between mb-0.5">
                  <label className="flex items-center gap-1 text-[9px] font-bold text-gray-500">
                    <Brain size={8} /> What to extract
                  </label>
                  <VariableDropdown
                    value=""
                    onChange={(name) => insertVariableForEntity(idx, name)}
                    flowVariables={flowVariables}
                    placeholder="Insert variable"
                    className="w-32"
                  />
                </div>
                <textarea
                  ref={(el) => { promptRefs.current[idx] = el; }}
                  value={ent.prompt}
                  onChange={(e) => updateEntity(idx, "prompt", e.target.value)}
                  rows={2}
                  className="w-full px-2 py-1.5 text-[10px] bg-white rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-purple-300 resize-none placeholder:text-gray-400"
                  placeholder="Extract the person's name from the message"
                />
              </div>

              <div className="mb-2">
                <label className="flex items-center gap-1 text-[9px] font-bold text-gray-500 mb-0.5">Fallback message (if missing)</label>
                <input
                  type="text"
                  value={ent.fallbackMessage}
                  onChange={(e) => updateEntity(idx, "fallbackMessage", e.target.value)}
                  placeholder="Please provide your name."
                  className="w-full px-2 py-1.5 text-[10px] bg-white rounded border border-gray-200 focus:outline-none focus:ring-1 focus:ring-purple-300 placeholder:text-gray-400"
                />
              </div>

              <div className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  id={`required-${ent.id}`}
                  checked={ent.required}
                  onChange={(e) => updateEntity(idx, "required", e.target.checked)}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-400"
                />
                <label htmlFor={`required-${ent.id}`} className="text-[9px] font-medium text-gray-600">Required</label>
              </div>
            </div>
          ))}
        </div>

        {/* Legacy single variable / prompt (when no entities) */}
        {!hasEntities && (
          <>
            <div>
              <label className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">
                <Variable size={10} />
                Save response in variable
              </label>
              <VariableDropdown
                value={form.variableName}
                onChange={(name) => updateField("variableName", name)}
                flowVariables={flowVariables}
                allowCreate
                onCreateVariable={onCreateVariable}
                placeholder="Select variable to store response..."
              />
            </div>
            <div>
              <label className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">
                <Brain size={10} />
                AI Extraction Prompt
              </label>
              <textarea
                value={form.aiPrompt}
                onChange={(e) => updateField("aiPrompt", e.target.value)}
                placeholder="Extract the person's name from the user's response. Return only the name."
                rows={3}
                className="w-full px-3 py-2 text-[12px] text-gray-800 bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-purple-400 resize-none placeholder:text-gray-400"
              />
              <p className="text-[9px] text-gray-400 mt-1">Instruct the AI what to extract. Or add entities above for multiple fields.</p>
            </div>
            <div>
              <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1 block">Expected answer type</label>
              <select
                value={form.answerType}
                onChange={(e) => updateField("answerType", e.target.value)}
                className="w-full px-3 py-2 text-[11px] font-semibold bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-purple-400"
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="email">Email</option>
                <option value="phone">Phone Number</option>
              </select>
            </div>
          </>
        )}
      </div>

      </NodeEditorTabs>
      <div className="flex gap-2 mt-3">
        <button
          onClick={handleSave}
          className="flex-1 py-2 text-[12px] font-bold bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm"
        >
          <Check size={14} />
          Save
        </button>
        <button
          onClick={onCancel}
          className="flex-1 py-2 text-[12px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Main AIAskQuestionNode ─────────────────────────────
const AIAskQuestionNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("aiAskQuestion");

  const handleSave = useCallback(
    (formData) => {
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, {
          ...data,
          ...formData,
        });
      }
      closeEditor();
    },
    [id, data]
  );

  const handleCancel = useCallback(() => {
    closeEditor();
  }, []);

  return (
    <div className={`relative transition-all shadow-md`} style={{ borderRadius: "12px" }}>
      <NodeActionButtons id={id} data={data} />
      {/* Target handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3.5 !h-3.5 !bg-purple-500 !border-2 !border-white !shadow-sm"
        style={{ left: -7 }}
      />

      {/* Content */}
      <CollapsedView data={data} onEdit={openEditor} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor
          data={data}
          onSave={handleSave}
          onCancel={handleCancel}
          flowVariables={data.flowVariables || []}
          onCreateVariable={data.onCreateVariable}
         presentation="fullscreen" onCancel={closeEditor} />
      </FlowNodeEditorShell>

      {/* Source handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="!w-3.5 !h-3.5 !bg-purple-500 !border-2 !border-white !shadow-sm"
        style={{ right: -7 }}
      />
    </div>
  );
};

export default AIAskQuestionNode;
